<script setup lang="ts">
/**
 * Полоса цифр над планом — макеты 9-1 (301:13760) и 9-10: гашение по дням,
 * средний цикл, просрочки и прогноз разбора стека.
 *
 * Без графических библиотек: четырнадцать столбиков — это четырнадцать div-ов,
 * и тянуть ради них чарт-движок значило бы платить сотней килобайт за то, что
 * умеет flex.
 *
 * Пустые дни рисуются серым огрызком, а не пропускаются: пропуск читался бы
 * как «данных нет», хотя данные есть и они — ноль.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { PlanStats } from '../api'

const props = defineProps<{
  stats: PlanStats | null
  /** Телефон: вместо четырёх карточек три пилюли строкой (макет 9-8) */
  compact?: boolean
}>()

const { t, locale } = useI18n()

/** Высота самого высокого столбика; на ней держится вся шкала */
const BAR_MAX = 48
/** Пустой день — огрызок: столбик нулевой высоты неотличим от отсутствия дня */
const BAR_MIN = 8

/** Дальше недели день недели уже не помогает: «к пт» какой именно пятницы? */
const WEEKDAY_HORIZON = 7

const number = (value: number, digits = 1) =>
  new Intl.NumberFormat(locale.value, { maximumFractionDigits: digits }).format(value)

const signed = (value: number) =>
  new Intl.NumberFormat(locale.value, {
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
  }).format(value)

const burndown = computed(() => {
  const days = props.stats?.burndown ?? []
  const peak = Math.max(1, ...days.map((day) => day.closed))

  return days.map((day) => ({
    ...day,
    height: day.closed ? Math.max(BAR_MIN, Math.round((day.closed / peak) * BAR_MAX)) : BAR_MIN,
  }))
})

/** «За неделю» — хвост окна: столько задач закрыто за последние семь дней */
const closedThisWeek = computed(() =>
  burndown.value.slice(-7).reduce((sum, day) => sum + day.closed, 0),
)

const cycleDays = computed(() => (props.stats?.medianCycleHours ?? 0) / 24)

/**
 * Тренд считаем только когда есть с чем сравнивать: разница с нулём прошлого
 * периода — это не «стало хуже на два дня», а «раньше нечего было мерить».
 */
const cycleTrend = computed(() => {
  const stats = props.stats

  if (!stats?.prevMedianCycleHours || !stats.medianCycleHours) return null

  return (stats.medianCycleHours - stats.prevMedianCycleHours) / 24
})

const forecast = computed(() => {
  const stats = props.stats

  if (!stats || !stats.stackSize) return { value: '—', hint: t('plan.widgets.forecast.empty') }

  if (!stats.closedPerDay || !stats.forecastDays) {
    return { value: '—', hint: t('plan.widgets.forecast.noPace') }
  }

  const days = Math.ceil(stats.forecastDays)
  const hint = `${t('plan.widgets.forecast.size', stats.stackSize)} · ${t('plan.widgets.forecast.pace', { pace: number(stats.closedPerDay) })}`

  if (days > WEEKDAY_HORIZON) {
    return { value: t('plan.widgets.forecast.days', days), hint }
  }

  const target = new Date()

  target.setDate(target.getDate() + days)

  return {
    value: t('plan.widgets.forecast.by', {
      day: new Intl.DateTimeFormat(locale.value, { weekday: 'short' }).format(target),
    }),
    hint,
  }
})
</script>

<template>
  <!--
    Телефон: гистограмма и подписи в 390 пикселей не встают, и вместо четырёх
    карточек остаются три цифры пилюлями (макет 9-8). Это не ужатая версия
    полосы, а другая её подача — поэтому отдельная разметка, а не медиазапрос
    поверх карточек.
  -->
  <div v-if="compact" class="pills">
    <span class="pill">{{ t('plan.widgets.burndown.week', { count: closedThisWeek }) }}</span>
    <span class="pill pill--danger" :class="{ 'pill--calm': !stats?.overdue }">
      {{ t('plan.widgets.overdue.pill', { count: stats?.overdue ?? 0 }) }}
    </span>
    <span class="pill">{{ forecast.value }}</span>
  </div>

  <div v-else class="widgets">
    <section class="widget widget--burndown">
      <header class="widget__head">
        <h2 class="widget__label">
          {{ t('plan.widgets.burndown.label', { days: stats?.burndownDays ?? 0 }) }}
        </h2>
        <span class="widget__aside">
          {{ t('plan.widgets.burndown.week', { count: closedThisWeek }) }}
        </span>
      </header>

      <div class="bars">
        <span
          v-for="day in burndown"
          :key="day.day"
          class="bars__bar"
          :class="{ 'bars__bar--empty': !day.closed }"
          :style="{ height: `${day.height}px` }"
          :title="`${day.day.slice(0, 10)}: ${day.closed}`"
        />
      </div>
    </section>

    <section class="widget">
      <h2 class="widget__label">
        {{ t('plan.widgets.cycle.label') }}
      </h2>
      <p class="widget__value">
        {{ stats?.cycleSample ? t('plan.widgets.cycle.value', { days: number(cycleDays) }) : '—' }}
      </p>
      <p class="widget__hint">
        <template v-if="stats?.cycleSample">
          {{ t('plan.widgets.cycle.hint', { days: stats.cycleDays }) }}
          <template v-if="cycleTrend !== null"> · {{ signed(cycleTrend) }}</template>
        </template>
        <template v-else>{{ t('plan.widgets.cycle.empty') }}</template>
      </p>
    </section>

    <section class="widget">
      <h2 class="widget__label">{{ t('plan.widgets.overdue.label') }}</h2>
      <p class="widget__value" :class="{ 'widget__value--danger': (stats?.overdue ?? 0) > 0 }">
        {{ stats?.overdue ?? 0 }}
      </p>
      <p class="widget__hint">
        {{ stats?.dueSoon ? t('plan.widgets.overdue.soon') : t('plan.widgets.overdue.calm') }}
      </p>
    </section>

    <section class="widget">
      <h2 class="widget__label">{{ t('plan.widgets.forecast.label') }}</h2>
      <p class="widget__value">{{ forecast.value }}</p>
      <p class="widget__hint">{{ forecast.hint }}</p>
    </section>
  </div>
</template>

<style scoped>
.widgets {
  display: flex;
  gap: 16px;
  align-items: stretch;
}

.widget {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

/* Ширина гистограммы — не доля, а сумма её столбиков: 14 × 16 + 13 × 6 + поля */
.widget--burndown {
  flex: 0 0 342px;
  gap: 10px;
}

.widget__head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.widget__label {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.02em;
  line-height: 14px;
  text-transform: uppercase;
}

.widget__aside {
  margin-left: auto;
  color: var(--color-text-primary);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  white-space: nowrap;
}

.widget__value {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  letter-spacing: -0.01em;
  line-height: 30px;
}

.widget__value--danger {
  color: var(--color-danger);
}

.widget__hint {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.bars {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 48px;
}

.bars__bar {
  width: 16px;
  border-radius: 3px;
  background: var(--color-primary);
}

.bars__bar--empty {
  background: var(--color-border-strong);
}

/* Телефон (макет 9-8): три пилюли строкой */
.pills {
  display: flex;
  gap: 8px;
}

.pill {
  padding: 5px 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
  line-height: 16px;
  white-space: nowrap;
}

.pill--danger {
  border-color: transparent;
  background: var(--color-danger-tint);
  color: var(--color-danger);
}

/* Ноль просрочек — не красная новость: пилюля возвращается к общему виду */
.pill--calm {
  border-color: var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
}
</style>
